'use client';

import {
    FetcherContext,
    useCalculationDetails,
    useCalculatorInputOptions,
} from '@copmer/calculator-integration/client';
import { VoyageResult } from '@copmer/frc-api-types';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { saveQuoteRequest } from '@/actions/calculator/bookable-vessels-user-facing-booking-functionality';
import { Accordion } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getCalculatorAPIRoutes } from '@/freight/api-routes';
import { reactQueryFetcher } from '@/lib/calculator/react-query-fetcher';
import { cn } from '@/lib/css-utils';
import { decodeResultsPageUrl } from '@/lib/quote-url-helpers';
import { Button } from '@/ui-lib/button';
import { Checkbox } from '@/ui-lib/form/checkbox';
import { FormController } from '@/ui-lib/form/form-controller';
import { renderTextInput, renderTextarea } from '@/ui-lib/form/form-input-renders';
import { Heading } from '@/ui-lib/heading';
import { Paragraph } from '@/ui-lib/paragraph';
import { TooltipProvider } from '@/ui-lib/tooltip';

import { ResultCard, columns } from './CalculationResults';
import { useCalculatorContextProvideValue } from './context';
import { CalculatorCurrencyProvider, useConvertedPlan } from './currency-context';
import { ErrorComponent } from './PlanDetailsPage';
import { QuoteFormSchema, QuoteFormSchemaT } from './quote-schema';
import { RequestQuotePageLoader, RequestQuotePageWrapper } from './RequestQuotePage.loader';

function RequestQuotePageInner({
    resultId,
    planId,
    fullPage,
    bookableVessels = [],
    email,
    phoneNumber,
    isModal = false,
    requestClose,
    onSuccessChange,
}: {
    resultId: string;
    planId: string;

    email: string;
    phoneNumber: string;

    fullPage?: boolean;
    bookableVessels?: string[];

    isModal?: boolean;
    requestClose?: () => void;

    onSuccessChange?: (success: boolean) => void;
}) {
    const apiRoutes = useMemo(() => {
        return getCalculatorAPIRoutes();
    }, []);

    const contextValue = useCalculatorContextProvideValue();

    const {
        vesselMap,
        waterTypes,

        isApiLoaded,
    } = useCalculatorInputOptions(contextValue.apiRoutes, null, false);

    const { data, isLoading, error } = useCalculationDetails(resultId, apiRoutes.calculationDetails);

    const rawPlan = useMemo(() => (data?.data?.plans ?? []).find((p: VoyageResult) => p.id === planId), [data, planId]);
    const plan = useConvertedPlan(rawPlan);

    const form = useForm<QuoteFormSchemaT>({
        defaultValues: {
            comments: '',
            agreeToTerms: false,
            email: email,
            phone: phoneNumber || '',
            calculationId: resultId,
            planId: planId,
        },
        resolver: zodResolver(QuoteFormSchema),
    });

    const [isSuccess, doSetIsSuccess] = useState(false);
    const setIsSuccess = (success: boolean) => {
        doSetIsSuccess(success);
        if (onSuccessChange) {
            onSuccessChange(success);
        }
    };

    const onSubmit = (values: QuoteFormSchemaT) => {
        // the quote links carry the calculator url the user came from
        const calculatorUrl = decodeResultsPageUrl(new URLSearchParams(window.location.search).get('rpUrl'));

        saveQuoteRequest({ ...values, calculatorUrl })
            .then((response) => {
                if (response.status === 'error') {
                    toast.error('Error requesting quote', {
                        description: response.error,
                    });
                    return;
                }

                setIsSuccess(true);
            })
            .catch((error: Error) => {
                toast.error('Error requesting quote', {
                    description: error.message || 'An unexpected error occurred.',
                });
            });
    };

    const [formEmail, formPhone] = useWatch({
        control: form.control,
        name: ['email', 'phone'],
    });

    let hasOverrides = false;

    let body = null;

    const showWithinContract = false;

    const renderedColumns = useMemo(
        () => columns.filter((column) => (showWithinContract ? true : column.key !== 'isWithinTolerance')),
        [showWithinContract]
    );

    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const openTermsModal = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setTermsModalOpen(true);
    };

    let phoneLabel = null;
    if (formPhone) {
        phoneLabel = <span>phone number ({formPhone})</span>;
    }

    let emailLabel = null;
    if (formEmail) {
        emailLabel = <span>email ({formEmail})</span>;
    }

    const contactLabel =
        phoneLabel && emailLabel ? (
            <>
                {phoneLabel} or {emailLabel}
            </>
        ) : phoneLabel || emailLabel ? (
            <>{phoneLabel || emailLabel}</>
        ) : null;

    if (error) {
        body = <ErrorComponent error={error} />;
    } else if (isLoading || !isApiLoaded) {
        body = <RequestQuotePageLoader />;
    } else if (!plan) {
        body = <ErrorComponent error="Plan not found" label="Not found" />;
    } else if (!bookableVessels || !bookableVessels.includes(plan.shipSize)) {
        body = <ErrorComponent error={'This plan is not bookable'} label="Not bookable" />;
    } else {
        hasOverrides = doesPlanHaveOverrides(plan);

        const totalWidth = renderedColumns.reduce((acc, column) => acc + column.width, 0);

        body = (
            <form className="flex flex-col gap-10" method="post" onSubmit={form.handleSubmit(onSubmit)}>
                <div>
                    <Accordion
                        type="single"
                        collapsible
                        className="mt-8 flex flex-col gap-4"
                        value={plan.id}
                        key={`accordion-${plan.id}`}
                    >
                        <TooltipProvider>
                            <ResultCard
                                key={plan.id}
                                result={plan}
                                vesselMap={vesselMap}
                                renderedColumns={renderedColumns}
                                totalWidth={totalWidth}
                                calculationId={resultId}
                                isBookable={false}
                                noArrow
                                isInQuotePage
                                baseColor="light"
                                highlightOverrides
                                waterTypes={waterTypes}
                            />
                        </TooltipProvider>
                    </Accordion>

                    {hasOverrides ? (
                        <Paragraph className="text-nWarning-500 text-right">
                            NB You have overwritten highlighted values, this might impact the Freight Rate.
                        </Paragraph>
                    ) : null}
                </div>

                <div className="flex flex-col gap-4">
                    <FormController
                        control={form.control}
                        field="email"
                        label="Email"
                        extraProps={{
                            type: 'email',
                        }}
                        renderInput={renderTextInput}
                        required={true}
                        placeholder="Enter your email address"
                        className={cn({
                            hidden: email !== '',
                        })}
                    />
                    <FormController
                        control={form.control}
                        field="phone"
                        label="Phone number"
                        extraProps={{
                            type: 'tel',
                        }}
                        renderInput={renderTextInput}
                        required={true}
                        placeholder="Enter your phone number"
                    />

                    <FormController
                        control={form.control}
                        field="comments"
                        label="Additional comments"
                        renderInput={renderTextarea}
                        required={false}
                        placeholder="Please provide any additional information or comments regarding your request."
                        extraProps={{
                            className: 'h-32',
                        }}
                    />
                </div>

                <div className="flex justify-end">
                    <Controller
                        control={form.control}
                        name="agreeToTerms"
                        render={({ field: { onChange, value }, fieldState }) => (
                            <Checkbox
                                checked={value}
                                onCheckedChange={(checked) => onChange(checked)}
                                hasError={!!fieldState?.error?.message}
                            >
                                I agree to the{' '}
                                <span role="button" onClick={openTermsModal} className="text-nInfo-500 underline">
                                    Terms and Conditions
                                </span>
                            </Checkbox>
                        )}
                    />
                </div>

                <div className="flex justify-end">
                    <Button variant="cta" type="submit">
                        Request a firm quote
                    </Button>
                </div>

                {isSuccess && (
                    <Dialog
                        open={isSuccess}
                        onOpenChange={(open) => {
                            if (!open) {
                                if (requestClose) {
                                    requestClose();
                                }

                                setIsSuccess(false);
                            }
                        }}
                    >
                        <DialogContent
                            theme="dark"
                            className="w-[calc(100%-2rem)] max-w-2xl max-h-[80%] overflow-y-auto"
                        >
                            <DialogHeader>
                                <DialogTitle>
                                    <Heading>Your request is sent</Heading>
                                </DialogTitle>
                            </DialogHeader>
                            <Paragraph>
                                Thank you for requesting a firm quote. We are reviewing your request and we&#39;ll get
                                back to you you as soon as possible on: {contactLabel}.
                            </Paragraph>
                            <Paragraph>
                                If you&#39;d like to discuss your request further, please contact our team:
                            </Paragraph>
                            <Paragraph>
                                <a href="mailto:freight@cmnavigator.com">freight@cmnavigator.com</a>
                            </Paragraph>
                        </DialogContent>
                    </Dialog>
                )}

                <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
                    <DialogContent theme="dark" className="w-[calc(100%-2rem)] max-w-2xl max-h-[80%] overflow-y-auto">
                        <DialogHeader className={'pb-6'}>
                            <DialogTitle className={'font-recoleta text-lg '}>Terms and Conditions</DialogTitle>
                        </DialogHeader>
                        <Paragraph>By submitting this quote request, I agree and acknowledge that:</Paragraph>
                        <ul className={'space-y-4 list-disc pl-4'}>
                            <li>
                                <Paragraph as={'span'}>
                                    The freight rate displayed is an indicative market level only. It is not a binding
                                    offer and may change due to market movements, vessel availability, or operational
                                    constraints until confirmed in writing by CM Navigator or its brokerage partners.
                                </Paragraph>
                            </li>
                            <li>
                                <Paragraph as={'span'}>
                                    The cargo details, load and discharge ports, laycan dates, and any other information
                                    I provide are accurate. I understand that any changes to this information may result
                                    in a different rate.
                                </Paragraph>
                            </li>
                            <li>
                                <Paragraph as={'span'}>
                                    CM Navigator and its authorised brokerage partners may act on my behalf for the sole
                                    purpose of sourcing freight rates, checking vessel availability, and presenting
                                    offers for my consideration.
                                </Paragraph>
                            </li>
                            <li>
                                <Paragraph as={'span'}>
                                    My contact details and shipment information may be shared with selected shipping
                                    companies or service providers only to prepare a quote. This information will be
                                    handled privately, securely and used solely for this purpose.
                                </Paragraph>
                            </li>
                            <li>
                                <Paragraph as={'span'}>
                                    CM Navigator cannot guarantee securing a vessel or matching the indicative rate
                                    shown. It accepts no liability for any losses, costs, or missed opportunities caused
                                    by delays, unavailability, or price changes between the time of my request and
                                    confirmation.
                                </Paragraph>
                            </li>
                            <li>
                                <Paragraph as={'span'}>
                                    Any disputes arising from this request or related transactions will be governed by
                                    the applicable law and jurisdiction of CM Navigator’s country of incorporation.
                                </Paragraph>
                            </li>
                        </ul>
                        <a
                            href="https://cmnavigator.blob.core.windows.net/documents/navi_merchants_draft_cp.pdf"
                            className={'text-nInfo-500 underline'}
                            target={'_blank'}
                            rel="noreferrer"
                        >
                            Navi Merchants Draft CP
                        </a>
                    </DialogContent>
                </Dialog>
            </form>
        );
    }

    return (
        <RequestQuotePageWrapper
            isModal={isModal}
            fullPage={fullPage}
            subtitle={
                contactLabel === null ? (
                    true
                ) : (
                    <>After you send the request, you will be contacted via your {contactLabel} as soon as possible.</>
                )
            }
        >
            {body}
        </RequestQuotePageWrapper>
    );
}

export const doesPlanHaveOverrides = (plan: VoyageResult) => {
    // Check if the plan has any overrides
    const overrides = plan.debugData?.inputParams?.overrides ?? {};

    // If overrides exist, return true
    if (overrides) {
        const ignoredKeys = ['hireCurrency', 'mgoCurrency', 'sfoCurrency', 'isOptimizer', 'isOptimizerProfitLoss'];

        // If theres a value in overrides that is not in ignoredKeys, return true
        for (const key of Object.keys(overrides)) {
            if (ignoredKeys.includes(key)) {
                continue;
            }

            if (
                overrides[key as keyof typeof overrides] !== undefined &&
                overrides[key as keyof typeof overrides] !== null
            ) {
                return true;
            }
        }
    }

    // // if any of the ports have overrides, return true
    const ports = plan.debugData?.portInfos ?? [];
    for (const port of ports) {
        // Check if the port has any overrides
        const portOverrides = port.overrides ?? {};

        const portResult = Object.keys(portOverrides).some(
            (key) => portOverrides[key as keyof typeof portOverrides] === true
        );

        if (portResult) {
            return true;
        }

        // Check if the operations have any overrides
        for (const operation of port.operations) {
            const overrides = operation.overrides ?? {};

            const ignoredOperationFields = ['cadenceInputType'];

            // If overrides exist, return true
            const result = Object.keys(overrides)
                .filter((key) => !ignoredOperationFields.includes(key))
                .some((key) => overrides[key as keyof typeof overrides] === true);

            if (result) {
                return true;
            }
        }
    }

    return false;
};

export default function RequestQuotePage({
    resultId,
    planId,
    fullPage = false,
    bookableVessels = [],
    email,
    phoneNumber,
    isModal = false,
    initialCurrency,
    requestClose,
    onSuccessChange,
}: {
    resultId: string;
    planId: string;

    email: string;
    phoneNumber: string;

    fullPage?: boolean;
    bookableVessels?: string[];

    isModal?: boolean;

    initialCurrency?: string;

    requestClose?: () => void;
    onSuccessChange?: (success: boolean) => void;
}) {
    return (
        <CalculatorCurrencyProvider initialCurrency={initialCurrency}>
            <FetcherContext.Provider value={reactQueryFetcher}>
                <RequestQuotePageInner
                    resultId={resultId}
                    planId={planId}
                    fullPage={fullPage}
                    bookableVessels={bookableVessels}
                    email={email}
                    phoneNumber={phoneNumber}
                    isModal={isModal}
                    requestClose={requestClose}
                    onSuccessChange={onSuccessChange}
                />
            </FetcherContext.Provider>
        </CalculatorCurrencyProvider>
    );
}
